import { LoadingState, LoadingStates } from 'globals/types';
import {
  applySnapshot,
  getSnapshot,
  Instance,
  SnapshotIn,
  types,
} from 'mobx-state-tree';
import { deepFlatten, formatDate, normalizeTimestamp } from 'globals/utils';
import { SingleAccountItemDataProps } from 'components/UserPanel/Pages/Report/ReportAccount/AccountDetailsSection/SingleAccountItem/SingleAccountItem';
import { RangeInput } from '@material-ui/pickers';
import { SingleBank, SingleBankProps } from './single-bank';
import { SingleAccountProps } from './single-account';

export interface BankAccountsListProps {
  available_banks: SingleBankProps[];
  available_date_from: number;
  available_date_to: number;
  status: LoadingState;
}

export const BankAccountsList = types
  .model({
    available_banks: types.optional(types.array(SingleBank), []),
    available_date_from: types.maybe(types.number),
    available_date_to: types.maybe(types.number),
    status: types.optional(
      types.enumeration(LoadingStates),
      LoadingState.INITIAL
    ),
  })
  .actions((self) => ({
    set(props: BankAccountsListProps): void {
      const currentSnapshot = getSnapshot(self);
      const newSnapshot = {
        ...currentSnapshot,
        ...props,
      };
      applySnapshot(self, newSnapshot);
    },
    updateStatus(status: LoadingState): void {
      const currentSnapshot = getSnapshot(self);
      const newSnapshot = {
        ...currentSnapshot,
        status,
      };
      applySnapshot(self, newSnapshot);
    },
  }))
  .views((self) => ({
    getSimpleBankList(): Array<{
      accounts: SingleAccountProps[];
      name: string;
      id: number;
    }> {
      return self.available_banks.map(({ account: accounts, name, id }) => {
        const sortedAccounts = accounts.slice().sort((a, b) => {
          if (a.currency < b.currency) {
            return -1;
          }
          if (a.currency > b.currency) {
            return 1;
          }
          return 0;
        });

        return {
          accounts: sortedAccounts,
          name,
          id,
        };
      });
    },
    getActiveAccounts(
      activeAccountId: string | undefined
    ): SingleAccountItemDataProps[] {
      const groupsOfAccounts = self.available_banks.map(
        ({ account: accounts, name, id }) =>
          accounts.map((account) => ({
            ...account,
            income: account.income.displayValue(),
            balance: account.balance.displayValue(),
            expenditure: account.expenditure.displayValue(),
            maturity: formatDate(account.maturity, true),
            opening: formatDate(account.opening, true),
            bankName: name,
            bankId: id,
          }))
      );
      return deepFlatten(groupsOfAccounts).filter(
        ({ id }) => id === activeAccountId
      );
    },
    getAvailableDates(): RangeInput<Date> {
      const { available_date_from: dateFrom, available_date_to: dateTo } = self;
      if (!dateFrom || !dateTo) return [null, null];
      return [normalizeTimestamp(dateFrom), normalizeTimestamp(dateTo)];
    },
  }));

export type AccountListSnapshot = SnapshotIn<typeof BankAccountsList>;
export type AccountListInstance = Instance<typeof BankAccountsList>;
